import { useState, useRef, useEffect, useCallback } from 'react';
import { APP } from './tokens';
import FolderPickerButton from './FolderPickerButton';

// ─── Types ──────────────────────────────────────

export interface TranscriptLine {
  time: string;
  text: string;
}

export interface AudioPlayerProps {
  fileName: string;
  /** e.g. "24:32 · 22.4 MB · MP3 · 128 kbps" */
  meta: string;
  /** Audio source URL (from URL.createObjectURL or server) */
  src?: string;
  transcript?: TranscriptLine[];
  onViewFullTranscript?: () => void;
  /** Chips at the bottom: "Transcribe", "Summarize", etc. */
  actions?: AudioAction[];
  onActionClick?: (label: string) => void;
  folders?: import('../../types').Folder[];
  currentFolderId?: string | null;
  onAddToFolder?: (folderId: string) => void;
  onCreateFolder?: (name: string) => Promise<string> | string;
  onDownload?: () => void;
}

export interface AudioAction {
  label: string;
  accent?: boolean;
}

const DEFAULT_ACTIONS: AudioAction[] = [
  { label: 'Transcribe' },
  { label: 'Summarize' },
  { label: 'Key moments' },
  { label: '+ Ask', accent: true },
];

// ─── Helpers ────────────────────────────────────

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Waveform visualization ─────────────────────

/** Generate deterministic pseudo-random bar heights from filename */
function generateBars(seed: string, count: number): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  const bars: number[] = [];
  for (let i = 0; i < count; i++) {
    hash = ((hash << 5) - hash + i * 7) | 0;
    const normalized = (Math.abs(hash) % 60) + 16; // 16–76px range
    bars.push(normalized);
  }
  return bars;
}

function Waveform({
  progress,
  onSeek,
}: {
  progress: number; // 0–1
  onSeek: (progress: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const bars = generateBars('waveform', 50);

  const handleClick = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    onSeek(Math.max(0, Math.min(1, x)));
  };

  return (
    <div
      ref={ref}
      onClick={handleClick}
      style={{
        width: '100%',
        height: 80,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {bars.map((h, i) => {
        const barProgress = i / bars.length;
        const played = barProgress < progress;
        return (
          <div
            key={i}
            style={{
              width: 3,
              height: h,
              borderRadius: 1.5,
              backgroundColor: played ? APP.waveformPlayed : APP.waveformUnplayed,
              flexShrink: 0,
              transition: 'background-color 0.1s',
            }}
          />
        );
      })}
      {/* Playhead dot */}
      <div
        style={{
          position: 'absolute',
          left: `${progress * 100}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: APP.playhead,
          border: `2px solid ${APP.playheadRing}`,
          boxShadow: `0 0 0 2px ${APP.playhead}`,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// ─── Playback controls ──────────────────────────

function SkipBackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={APP.textAudioTitle} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polygon points="19 20 9 12 19 4 19 20" />
      <line x1="5" y1="19" x2="5" y2="5" />
    </svg>
  );
}

function SkipForwardIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={APP.textAudioTitle} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polygon points="5 4 15 12 5 20 5 4" />
      <line x1="19" y1="5" x2="19" y2="19" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <rect x="6" y="4" width="4" height="16" fill="#FFFFFF" stroke="none" />
      <rect x="14" y="4" width="4" height="16" fill="#FFFFFF" stroke="none" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFFFFF" stroke="none" style={{ flexShrink: 0 }}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  );
}

function VolumeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={APP.textTimestamp} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="none" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}

// ─── Main component ─────────────────────────────

export default function AudioPlayer({
  fileName,
  meta,
  src,
  transcript,
  onViewFullTranscript,
  actions = DEFAULT_ACTIONS,
  onActionClick,
  folders = [],
  currentFolderId,
  onAddToFolder,
  onCreateFolder,
  onDownload,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(0.8);

  // Create audio element
  useEffect(() => {
    if (!src) return;
    const audio = new Audio(src);
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('ended', () => setPlaying(false));

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [src]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying(!playing);
  }, [playing]);

  const handleSeek = useCallback((progress: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = progress * duration;
    setCurrentTime(audio.currentTime);
  }, [duration]);

  const handleSkipBack = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
  }, []);

  const handleSkipForward = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(audio.duration, audio.currentTime + 10);
  }, []);

  const cycleSpeed = useCallback(() => {
    const speeds = [1, 1.5, 2, 0.5];
    const nextIdx = (speeds.indexOf(speed) + 1) % speeds.length;
    const next = speeds[nextIdx];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  }, [speed]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        backgroundColor: APP.bgDocColumn,
        borderRight: `1px solid ${APP.border}`,
      }}
    >
      {/* ── Top Bar ─────────────────────────── */}
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          paddingInline: 16,
          backgroundColor: APP.bgContent,
          borderBottom: `1px solid ${APP.borderDocTopBar}`,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: APP.textCardDetail,
            fontFamily: APP.fontFamily,
            lineHeight: '16px',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={onDownload}
            style={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2v7M4 6.5L7 10l3-3.5M2 11h10" stroke={APP.iconDefault} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <FolderPickerButton
            folders={folders}
            currentFolderId={currentFolderId}
            onSelect={(folderId) => onAddToFolder?.(folderId)}
            onCreateFolder={onCreateFolder}
          />
        </div>
      </div>

      {/* ── Player Area ──────────────────────── */}
      <div
        style={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '0%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 40,
        }}
      >
        <div style={{ width: 600, display: 'flex', flexDirection: 'column', gap: 32, flexShrink: 0 }}>
          {/* File info row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Audio icon */}
            <div
              style={{
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                borderRadius: 12,
                backgroundColor: APP.bgAudioIcon,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M9 18V5l12-2v13" />
                <circle cx="6" cy="18" r="3" fill="none" />
                <circle cx="18" cy="16" r="3" fill="none" />
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: APP.textAudioTitle,
                  fontFamily: APP.fontFamily,
                  lineHeight: '20px',
                }}
              >
                {fileName}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: APP.textAudioMeta,
                  fontFamily: APP.fontFamily,
                  lineHeight: '16px',
                }}
              >
                {meta}
              </span>
            </div>
          </div>

          {/* Waveform */}
          <Waveform progress={progress} onSeek={handleSeek} />

          {/* Controls section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Time row */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  fontVariantNumeric: 'tabular-nums',
                  color: APP.textTimestamp,
                  fontFamily: APP.fontFamily,
                  lineHeight: '16px',
                  flexShrink: 0,
                }}
              >
                {formatTime(currentTime)}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  fontVariantNumeric: 'tabular-nums',
                  color: APP.textTimestamp,
                  fontFamily: APP.fontFamily,
                  lineHeight: '16px',
                  flexShrink: 0,
                }}
              >
                {duration > 0 ? formatTime(duration) : meta.split('·')[0]?.trim() ?? '0:00'}
              </span>
            </div>

            {/* Play/skip controls */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
              <button
                type="button"
                onClick={handleSkipBack}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <SkipBackIcon />
              </button>
              <button
                type="button"
                onClick={togglePlay}
                style={{
                  width: 52,
                  height: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: APP.bgPlayBtn,
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                {playing ? <PauseIcon /> : <PlayIcon />}
              </button>
              <button
                type="button"
                onClick={handleSkipForward}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <SkipForwardIcon />
              </button>
            </div>

            {/* Speed + volume row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button
                type="button"
                onClick={cycleSpeed}
                style={{
                  borderRadius: 6,
                  paddingBlock: 4,
                  paddingInline: 10,
                  backgroundColor: APP.bgSpeedBtn,
                  border: `1px solid ${APP.borderSpeedBtn}`,
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 16,
                  fontWeight: 400,
                  color: '#000000',
                  lineHeight: '20px',
                }}
              >
                {speed}× speed
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <VolumeIcon />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={handleVolumeChange}
                  style={{ width: 64, accentColor: APP.textAudioTitle }}
                />
              </div>
            </div>
          </div>

          {/* Transcript section */}
          {transcript && transcript.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingTop: 20,
                gap: 10,
                borderTop: `1px solid ${APP.borderSpeedBtn}`,
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase' as const,
                    color: APP.textTranscriptLabel,
                    fontFamily: APP.fontFamily,
                    lineHeight: '16px',
                    flexShrink: 0,
                  }}
                >
                  Transcript
                </span>
                {onViewFullTranscript && (
                  <button
                    type="button"
                    onClick={onViewFullTranscript}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 12,
                      fontWeight: 500,
                      color: APP.textViewFull,
                      fontFamily: APP.fontFamily,
                      lineHeight: '16px',
                      flexShrink: 0,
                    }}
                  >
                    View full
                  </button>
                )}
              </div>

              {/* Transcript lines */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {transcript.map((line, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: APP.textTranscriptTime,
                        fontFamily: APP.fontFamily,
                        lineHeight: '14px',
                        flexShrink: 0,
                        paddingTop: 1,
                        width: 'fit-content',
                      }}
                    >
                      {line.time}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: APP.textTranscriptBody,
                        fontFamily: APP.fontFamily,
                        lineHeight: '20px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {line.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Audio Action Chips ────────────────── */}
      <div
        style={{
          height: 44,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          paddingInline: 16,
          gap: 8,
          backgroundColor: APP.bgAudioActionBar,
          borderTop: `1px solid ${APP.border}`,
        }}
      >
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={() => onActionClick?.(action.label)}
            style={{
              borderRadius: 20,
              paddingBlock: 5,
              paddingInline: 12,
              backgroundColor: APP.bgAudioChip,
              border: `1px solid ${APP.borderAudioChip}`,
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 400,
              color: action.accent ? APP.textAudioChipAccent : APP.textAudioChip,
              lineHeight: '16px',
            }}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}
